import { Navigate, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Fragment, ReactNode, useEffect } from "react";
import { useUserRole } from "../utils/hooks/useUserRole";
import { canAccessGDP, featureAccess } from "../permissions/access";
import { useForbidden } from "../permissions/forbiddenContext";
import { logout } from "../services/keycloak";
import Button from "@mui/material/Button";

import PermissionDeniedPage from "./PermissionDeniedPage";
import HomePage from "../pages/HomePage";
import UserManagementPage from "../pages/UserManagementPage";
import ReportPage from "../pages/ReportPage";
import ReportDetailsPage from "../pages/ReportDetailsPage";
import SaleReportMoreDetailsPage from "../pages/SaleReportMoreDetailsPage";
import FixWastePage from "../pages/FixWastePage";
import SalesPage from "../pages/SalesPage";
import InvoiceDetailsPage from "../pages/InvoiceDetailsPage";
import CashSalesDetailsPage from "../pages/CashSalesDetailsPage";
import CustomerPage from "../pages/CustomerPage";
import ProductPage from "../pages/ProductPage";
import PriceBookPage from "../pages/PriceBookPage";
import ProductDetailsPage from "../pages/ProductDetailsPage";
import PriceBookDetailsPage from "../pages/PriceBookDetailsPage";
import ProductCostPage from "../pages/ProductCostPage";
import PricePeriodPage from "../pages/PricePeriodPage";
import CustomerDetailsPage from "../pages/CustomerDetailsPage";
import ETaxPage from "../pages/ETaxPage";
import ETaxSendPage from "../pages/ETaxSendPage";
import ETaxNoteSendPage from "../pages/ETaxNoteSendPage";
import ETaxDetailsPage from "../pages/ETaxDetailsPage";
import ETaxHistoryPage from "../pages/ETaxHistoryPage";
import ETaxBatchDetailsPage from "../pages/ETaxBatchDetailsPage";
import ETaxAutoSendPage from "../pages/ETaxAutoSendPage";
import DeductionPage from "../pages/DeductionPage";
import DeductionRouteDetailPage from "../pages/DeductionRouteDetailPage";
import DeductionStaffSummaryPage from "../pages/DeductionStaffSummaryPage";
import EventLogsPage from "../pages/EventLogsPage";
import ExpressDataPage from "../pages/ExpressDataPage";
import CreditDebitNoteListPage from "../pages/CreditDebitNoteListPage";
import CreditDebitNoteFormPage from "../pages/CreditDebitNoteFormPage";
import CreditDebitNoteDetailPage from "../pages/CreditDebitNoteDetailPage";
import ExpressDocumentListPage from "../pages/ExpressDocumentListPage";
import ExpressDocumentFormPage from "../pages/ExpressDocumentFormPage";
import ExpressContactPage from "../pages/ExpressContactPage";
import ExpressAccountPage from "../pages/ExpressAccountPage";
import DailyExpenseListPage from "../pages/DailyExpenseListPage";
import DailyExpenseFormPage from "../pages/DailyExpenseFormPage";
import DailyExpenseReviewPage from "../pages/DailyExpenseReviewPage";
import MonthlyExpenseClosePage from "../pages/MonthlyExpenseClosePage";
import MonthEstimatePage from "../pages/MonthEstimatePage";
import RouteSalesTargetPage from "../pages/RouteSalesTargetPage";
import SalesReportExportPage from "../pages/SalesReportExportPage";
import Gb1SaleReportPage from "../pages/Gb1SaleReportPage";
import CompanySalesReportPage from "../pages/CompanySalesReportPage";
import { COMPANY_SALES_PATH } from "./CompanySalesReport/report";
import SaleDepositReportPage from "../pages/SaleDepositReportPage";
import SaleDepositBranchDayPage from "../pages/SaleDepositBranchDayPage";
import SaleDepositEditRequestPage from "../pages/SaleDepositEditRequestPage";
import QrTransactionPage from "../pages/QrTransactionPage";
import QrDashboardPage from "../pages/QrDashboardPage";
import VisitFrequencyPage from "../pages/VisitFrequencyPage";
import InactiveStoresPage from "../pages/InactiveStoresPage";
import StockUploadPage from "../pages/StockUploadPage";
import PathwayUploadPage from "../pages/PathwayUploadPage";
import RouteManagementPage from "../pages/RouteManagementPage";
import ReprintAuthPage from "../pages/ReprintAuthPage";
import ReprintRequestPage from "../pages/ReprintRequestPage";
import FormulaScorerPage from "../pages/FormulaScorerPage";
import PalletSummaryPage from "../pages/PalletSummaryPage";
import WastePercentReportPage from "../pages/WastePercentReportPage";
import IncomeExpenseReportPage from "../pages/IncomeExpenseReportPage";
import PivotPage from "../pages/PivotPage";
import PurchaseOrderListPage from "../pages/PurchaseOrderListPage";
import PurchaseOrderImportPage from "../pages/PurchaseOrderImportPage";
import BranchPurchaseOrderPage from "../pages/BranchPurchaseOrderPage";
import SpecialOrderListPage from "../pages/SpecialOrderListPage";
import SpecialOrderFormPage from "../pages/SpecialOrderFormPage";
import SpecialOrderDetailPage from "../pages/SpecialOrderDetailPage";
import DemandFactorPage from "../pages/DemandFactorPage";
import BranchListPage from "../pages/BranchListPage";
import BranchDetailPage from "../pages/BranchDetailPage";
import VehiclePage from "../pages/VehiclePage";
import SystemSettingsPage from "../pages/SystemSettingsPage";
import BdcImportPage from "../pages/BdcImportPage";
import BdcQueuePage from "../pages/BdcQueuePage";
import BdcShipmentPage from "../pages/BdcShipmentPage";
import BdcSettingsPage from "../pages/BdcSettingsPage";
import BdcStockDayPage from "../pages/BdcStockDayPage";
import BdcDispatchPage from "../pages/BdcDispatchPage";
import BdcDocumentsPage from "../pages/BdcDocumentsPage";
import BdcInvoicesPage from "../pages/BdcInvoicesPage";
import BdcEdiPage from "../pages/BdcEdiPage";
import BdcCreditNotesPage from "../pages/BdcCreditNotesPage";
import BdcCreditNotePage from "../pages/BdcCreditNotePage";
import BdcBillingsPage from "../pages/BdcBillingsPage";
import BdcOnlyRoutes from "./Bdc/BdcOnlyRoutes";
import BdcDashboardPage from "../pages/BdcDashboardPage";
import BdcDashboardStoresPage from "../pages/BdcDashboardStoresPage";
import { BDC_DASHBOARD_PATH, BDC_DASHBOARD_STORES_PATH } from "./Bdc/Dashboard/constants";
import {
  BDC_BILLINGS_PATH,
  BDC_CREDIT_NOTE_PATH,
  BDC_CREDIT_NOTES_PATH,
  BDC_DOCUMENTS_PATH,
  BDC_EDI_PATH,
  BDC_IMPORT_PATH,
  BDC_INVOICES_PATH,
  BDC_PATH,
  BDC_QUEUE_PATH,
  BDC_SETTINGS_PATH,
  BDC_SHIPMENT_PATH,
  BDC_STOCK_DAY_PATH,
  BDC_DISPATCH_PATH,
} from "./Bdc/constants";
import { SYSTEM_SETTINGS_PATH } from "./SystemSettings/constants";
import {
  EXPRESS_DATA_PATH,
  LEGACY_EXPRESS_DATA_PATH,
} from "./ExpressData/constants";
import {
  CREDIT_DEBIT_NOTE_NEW_PATH,
  CREDIT_DEBIT_NOTE_PATH,
} from "./CreditDebitNote/constants";
import {
  EXPRESS_DOC_TYPES,
  EXPRESS_DOC_TYPE_CONFIG,
  documentNewPath,
} from "./ExpressDocument/constants";
import { EXPRESS_ACCOUNT_PATH, EXPRESS_CONTACT_PATH } from "./ExpressMaster/constants";
import {
  DAILY_EXPENSE_DAY_PATH,
  DAILY_EXPENSE_PATH,
  DAILY_EXPENSE_REVIEW_PATH,
  MONTHLY_EXPENSE_CLOSE_PATH,
  MONTH_ESTIMATE_PATH,
  ROUTE_SALES_TARGET_PATH,
} from "./DailyExpense/constants";
import {
  SALE_DEPOSIT_BRANCH_DAY_PATH,
  SALE_DEPOSIT_EDIT_REQUEST_PATH,
  SALE_DEPOSIT_REPORT_PATH,
} from "./SaleDepositReport/depositReportUrl";
import {
  PRICE_PERIOD_PATH,
  PRODUCT_COST_PATH,
} from "./Product/shared/constants";
import { DEMAND_FACTOR_PATH } from "./DemandFactor/constants";
import { BRANCH_LIST_PATH } from "./Branch/constants";
import { VEHICLES_PATH } from "./Vehicle/vehiclePaths";
import {
  INACTIVE_STORES_PATH,
  VISIT_FREQUENCY_PATH,
} from "./VisitFrequency/reportPaths";
import { ROUTE_MANAGEMENT_PATH } from "./RouteManagement/routeText";
import { PALLET_SUMMARY_PATH } from "./PalletSummary/constants";
import { WASTE_PERCENT_REPORT_PATH } from "./WastePercentReport/constants";
import { INCOME_EXPENSE_REPORT_PATH } from "./IncomeExpenseReport/constants";
import { ETAX_NOTE_SEND_PATH } from "./ETax/sendPaths";

interface RequirePermissionProps {
  allow: boolean;
  children: ReactNode;
}

// Route guard: renders the page when allowed, otherwise replaces it with the
// standard Permission Denied page. This blocks direct URL access, not just
// menu navigation.
const RequirePermission = ({ allow, children }: RequirePermissionProps) => {
  if (!allow) return <PermissionDeniedPage />;

  return <>{children}</>;
};

function Routing() {
  const role = useUserRole();
  const location = useLocation();
  const navigate = useNavigate();
  const { isForbidden, clearForbidden } = useForbidden();

  // An API 403 marks the app as forbidden. Clear it whenever the route changes
  // so the user is never stuck on the Permission Denied page after navigating.
  useEffect(() => {
    clearForbidden();
  }, [location.pathname, clearForbidden]);

  // 1) App-level gate: roles without GDP access (e.g. the Sale role) are
  //    blocked entirely — every path shows the Permission Denied page.
  if (!canAccessGDP()) {
    return (
      <PermissionDeniedPage
        hideHomeButton
        action={
          <Button
            variant="contained"
            sx={{ mt: 1 }}
            onClick={() => logout()}
          >
            ออกจากระบบ
          </Button>
        }
      />
    );
  }

  // 2) The API returned 403 for the current feature/page: replace the content
  //    with the Permission Denied page instead of a stuck skeleton/loading.
  if (isForbidden) {
    return (
      <PermissionDeniedPage
        action={
          <Button
            variant="contained"
            sx={{ mt: 1 }}
            onClick={() => {
              clearForbidden();
              navigate("/");
            }}
          >
            กลับหน้าหลัก
          </Button>
        }
      />
    );
  }

  // 3) A BDC-only user (bdc manager / bdc operator and no other role) reaches the BDC screens
  //    and nothing else: the dashboard at "/" and every other path land on the BDC queue, so
  //    no screen opens onto calls the server refuses them.
  if (role.isBdcOnly) return <BdcOnlyRoutes />;

  // One list, one new and one view/edit route per type; keyed by type so moving between the
  // types' pages starts each page afresh.
  const canUseExpressDocuments = featureAccess.expressDocument(role);
  const expressDocumentRoutes = EXPRESS_DOC_TYPES.map((docType) => {
    const config = EXPRESS_DOC_TYPE_CONFIG[docType];
    return (
      <Fragment key={docType}>
        <Route
          path={config.path}
          element={
            <RequirePermission allow={canUseExpressDocuments}>
              <ExpressDocumentListPage key={docType} docType={docType} />
            </RequirePermission>
          }
        />
        <Route
          path={documentNewPath(config)}
          element={
            <RequirePermission allow={canUseExpressDocuments}>
              <ExpressDocumentFormPage key={docType} docType={docType} />
            </RequirePermission>
          }
        />
        <Route
          path={`${config.path}/:uuid`}
          element={
            <RequirePermission allow={canUseExpressDocuments}>
              <ExpressDocumentFormPage key={docType} docType={docType} />
            </RequirePermission>
          }
        />
      </Fragment>
    );
  });

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />

      {/* User Management */}
      <Route
        path="/user-management"
        element={
          <RequirePermission allow={featureAccess.userManagement(role)}>
            <UserManagementPage />
          </RequirePermission>
        }
      />

      {/* Report */}
      <Route path="/report/sale" element={<ReportPage />} />
      <Route
        path="/report/sales-report"
        element={
          <RequirePermission allow={featureAccess.salesReportExport(role)}>
            <SalesReportExportPage />
          </RequirePermission>
        }
      />
      <Route
        path={COMPANY_SALES_PATH}
        element={
          <RequirePermission allow={featureAccess.companySalesReport(role)}>
            <CompanySalesReportPage />
          </RequirePermission>
        }
      />
      <Route
        path="/report/gb1-sale-report"
        element={
          <RequirePermission allow={featureAccess.gb1SaleReport(role)}>
            <Gb1SaleReportPage />
          </RequirePermission>
        }
      />
      <Route
        path={WASTE_PERCENT_REPORT_PATH}
        element={
          <RequirePermission allow={featureAccess.wastePercentReport(role)}>
            <WastePercentReportPage />
          </RequirePermission>
        }
      />
      <Route
        path={INCOME_EXPENSE_REPORT_PATH}
        element={
          <RequirePermission allow={featureAccess.incomeExpenseReport(role)}>
            <IncomeExpenseReportPage />
          </RequirePermission>
        }
      />
      <Route
        path={SALE_DEPOSIT_REPORT_PATH}
        element={
          <RequirePermission allow={featureAccess.saleDepositReport(role)}>
            <SaleDepositReportPage />
          </RequirePermission>
        }
      />
      {/* The branch-day drill-down out of the summary. Same guard as the report it comes
          from: it shows the same records, narrowed to one branch and one day. */}
      <Route
        path={SALE_DEPOSIT_BRANCH_DAY_PATH}
        element={
          <RequirePermission allow={featureAccess.saleDepositReport(role)}>
            <SaleDepositBranchDayPage />
          </RequirePermission>
        }
      />
      {/* คำขอแก้ไขยอดโอน — the report's second tab, addressed as a SIBLING of it rather than
          as a child, so it can never be read as a branch code by the drill-down pattern above.
          Guarded by the report's own cohort: the queue holds the same records at one remove,
          the branch reads it to follow its own request, and only the head-office accounting
          supervisor gets the decide controls inside it. */}
      <Route
        path={SALE_DEPOSIT_EDIT_REQUEST_PATH}
        element={
          <RequirePermission allow={featureAccess.saleDepositReport(role)}>
            <SaleDepositEditRequestPage />
          </RequirePermission>
        }
      />
      <Route
        path="/report/qr-transaction"
        element={
          <RequirePermission allow={featureAccess.qrTransaction(role)}>
            <QrTransactionPage />
          </RequirePermission>
        }
      />
      <Route
        path="/report/qr-dashboard"
        element={
          <RequirePermission allow={featureAccess.qrAnalytics(role)}>
            <QrDashboardPage />
          </RequirePermission>
        }
      />
      <Route
        path={VISIT_FREQUENCY_PATH}
        element={
          <RequirePermission allow={featureAccess.visitFrequency(role)}>
            <VisitFrequencyPage />
          </RequirePermission>
        }
      />
      <Route
        path={INACTIVE_STORES_PATH}
        element={
          <RequirePermission allow={featureAccess.visitFrequency(role)}>
            <InactiveStoresPage />
          </RequirePermission>
        }
      />
      <Route
        path="/report/formula-scorer"
        element={
          <RequirePermission allow={featureAccess.formulaScorer(role)}>
            <FormulaScorerPage />
          </RequirePermission>
        }
      />
      <Route
        path={PALLET_SUMMARY_PATH}
        element={
          <RequirePermission allow={featureAccess.palletSummary(role)}>
            <PalletSummaryPage />
          </RequirePermission>
        }
      />
      <Route
        path="/report/pivot"
        element={
          <RequirePermission allow={featureAccess.pivot(role)}>
            <PivotPage />
          </RequirePermission>
        }
      />
      <Route
        path="/report/sale/details/:reportId"
        element={<ReportDetailsPage />}
      />
      {/* New: pathway daily-grid detail (separate from the existing details page) */}
      <Route
        path="/report/sale/more-details/:uuid"
        element={<SaleReportMoreDetailsPage />}
      />
      <Route
        path="/report/sale/details/:reportId/:checkInId/:reportCode/:version/fix-waste"
        element={<FixWastePage />}
      />

      {/* Sales */}
      <Route path="/sales" element={<SalesPage />} />
      <Route
        path="/sales/invoice/details/:invoiceId"
        element={<InvoiceDetailsPage />}
      />
      <Route
        path="/sales/cash-sales/details/:cashSalesId"
        element={<CashSalesDetailsPage />}
      />
      <Route
        path="/sales/reprint-authorization"
        element={
          <RequirePermission allow={featureAccess.reprintAuth(role)}>
            <ReprintAuthPage />
          </RequirePermission>
        }
      />
      <Route
        path="/sales/reprint-requests"
        element={
          <RequirePermission allow={featureAccess.reprintRequest(role)}>
            <ReprintRequestPage />
          </RequirePermission>
        }
      />

      {/* ออเดอร์พิเศษ — list (+ ปิดวันสายพิเศษ tab), create, detail, edit draft */}
      <Route
        path="/special-orders"
        element={
          <RequirePermission allow={featureAccess.specialOrder(role)}>
            <SpecialOrderListPage />
          </RequirePermission>
        }
      />
      <Route
        path="/special-orders/new"
        element={
          <RequirePermission allow={featureAccess.specialOrder(role)}>
            <SpecialOrderFormPage />
          </RequirePermission>
        }
      />
      <Route
        path="/special-orders/:uuid"
        element={
          <RequirePermission allow={featureAccess.specialOrder(role)}>
            <SpecialOrderDetailPage />
          </RequirePermission>
        }
      />
      <Route
        path="/special-orders/:uuid/edit"
        element={
          <RequirePermission allow={featureAccess.specialOrder(role)}>
            <SpecialOrderFormPage />
          </RequirePermission>
        }
      />

      {/* Customer */}
      <Route path="/customer" element={<CustomerPage />} />
      <Route
        path="/customer/details/:customerId"
        element={<CustomerDetailsPage />}
      />

      {/* Product */}
      <Route
        path="/product/products"
        element={
          <RequirePermission allow={featureAccess.product(role)}>
            <ProductPage />
          </RequirePermission>
        }
      />
      <Route
        path="/product/products/details/:productId"
        element={
          <RequirePermission allow={featureAccess.product(role)}>
            <ProductDetailsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/product/price-book"
        element={
          <RequirePermission allow={featureAccess.product(role)}>
            <PriceBookPage />
          </RequirePermission>
        }
      />
      <Route
        path="/product/price-book/details/:pricebookId"
        element={
          <RequirePermission allow={featureAccess.product(role)}>
            <PriceBookDetailsPage />
          </RequirePermission>
        }
      />
      <Route
        path={PRODUCT_COST_PATH}
        element={
          <RequirePermission allow={featureAccess.productCostRead(role)}>
            <ProductCostPage />
          </RequirePermission>
        }
      />
      <Route
        path={PRICE_PERIOD_PATH}
        element={
          <RequirePermission allow={featureAccess.pricePeriodRead(role)}>
            <PricePeriodPage />
          </RequirePermission>
        }
      />

      {/* E-Tax */}
      <Route
        path="/e-tax"
        element={
          <RequirePermission allow={featureAccess.eTax(role)}>
            <ETaxPage />
          </RequirePermission>
        }
      />
      <Route
        path="/e-tax/send"
        element={
          <RequirePermission allow={featureAccess.eTax(role)}>
            <ETaxSendPage />
          </RequirePermission>
        }
      />
      <Route
        path={ETAX_NOTE_SEND_PATH}
        element={
          <RequirePermission allow={featureAccess.eTax(role)}>
            <ETaxNoteSendPage />
          </RequirePermission>
        }
      />
      <Route
        path="/e-tax/details"
        element={
          <RequirePermission allow={featureAccess.eTax(role)}>
            <ETaxDetailsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/e-tax/history"
        element={
          <RequirePermission allow={featureAccess.eTax(role)}>
            <ETaxHistoryPage />
          </RequirePermission>
        }
      />
      <Route
        path="/e-tax/history/:batchId"
        element={
          <RequirePermission allow={featureAccess.eTax(role)}>
            <ETaxBatchDetailsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/e-tax/auto-send"
        element={
          <RequirePermission allow={featureAccess.eTax(role)}>
            <ETaxAutoSendPage />
          </RequirePermission>
        }
      />

      {/* Deduction Report */}
      <Route path="/report/deduction" element={<DeductionPage />} />
      <Route
        path="/report/deduction/route/:routeUuid"
        element={<DeductionRouteDetailPage />}
      />
      <Route
        path="/report/deduction/staff/:staffUuid"
        element={<DeductionStaffSummaryPage />}
      />

      {/* Express — ข้อมูล Express (moved out of รายงาน; the old path redirects) and
          ใบลดหนี้/ใบเพิ่มหนี้: list, create, detail */}
      <Route
        path={EXPRESS_DATA_PATH}
        element={
          <RequirePermission allow={featureAccess.expressData(role)}>
            <ExpressDataPage />
          </RequirePermission>
        }
      />
      <Route
        path={LEGACY_EXPRESS_DATA_PATH}
        element={<Navigate to={EXPRESS_DATA_PATH} replace />}
      />
      <Route
        path={CREDIT_DEBIT_NOTE_PATH}
        element={
          <RequirePermission allow={featureAccess.creditDebitNote(role)}>
            <CreditDebitNoteListPage />
          </RequirePermission>
        }
      />
      <Route
        path={CREDIT_DEBIT_NOTE_NEW_PATH}
        element={
          <RequirePermission allow={featureAccess.creditDebitNote(role)}>
            <CreditDebitNoteFormPage />
          </RequirePermission>
        }
      />
      <Route
        path={`${CREDIT_DEBIT_NOTE_PATH}/:uuid`}
        element={
          <RequirePermission allow={featureAccess.creditDebitNote(role)}>
            <CreditDebitNoteDetailPage />
          </RequirePermission>
        }
      />

      {/* Express entry documents — ซื้อเชื่อ, ซื้อสด, รายได้อื่นๆ, รับชำระหนี้: list, new, view/edit */}
      {expressDocumentRoutes}
      {/* The Express code lists head office keeps */}
      <Route
        path={EXPRESS_CONTACT_PATH}
        element={
          <RequirePermission allow={featureAccess.expressMaster(role)}>
            <ExpressContactPage />
          </RequirePermission>
        }
      />
      <Route
        path={EXPRESS_ACCOUNT_PATH}
        element={
          <RequirePermission allow={featureAccess.expressMaster(role)}>
            <ExpressAccountPage />
          </RequirePermission>
        }
      />

      {/* ค่าใช้จ่าย — รับจ่าย: the branch's daily expenses (list, one day) and head office's review */}
      <Route
        path={DAILY_EXPENSE_PATH}
        element={
          <RequirePermission allow={featureAccess.dailyExpense(role)}>
            <DailyExpenseListPage />
          </RequirePermission>
        }
      />
      <Route
        path={DAILY_EXPENSE_DAY_PATH}
        element={
          <RequirePermission allow={featureAccess.dailyExpense(role)}>
            <DailyExpenseFormPage />
          </RequirePermission>
        }
      />
      <Route
        path={DAILY_EXPENSE_REVIEW_PATH}
        element={
          <RequirePermission allow={featureAccess.dailyExpenseReview(role)}>
            <DailyExpenseReviewPage />
          </RequirePermission>
        }
      />
      <Route
        path={MONTHLY_EXPENSE_CLOSE_PATH}
        element={
          <RequirePermission allow={featureAccess.monthlyExpenseClose(role)}>
            <MonthlyExpenseClosePage />
          </RequirePermission>
        }
      />
      <Route
        path={MONTH_ESTIMATE_PATH}
        element={
          <RequirePermission allow={featureAccess.monthEstimate(role)}>
            <MonthEstimatePage />
          </RequirePermission>
        }
      />
      <Route
        path={ROUTE_SALES_TARGET_PATH}
        element={
          <RequirePermission allow={featureAccess.routeSalesTarget(role)}>
            <RouteSalesTargetPage />
          </RequirePermission>
        }
      />

      {/* สาขา — branch list, create, and the per-branch readiness checklist */}
      <Route
        path={BRANCH_LIST_PATH}
        element={
          <RequirePermission allow={featureAccess.branchManagement(role)}>
            <BranchListPage />
          </RequirePermission>
        }
      />
      <Route
        path={`${BRANCH_LIST_PATH}/:uuid`}
        element={
          <RequirePermission allow={featureAccess.branchManagement(role)}>
            <BranchDetailPage />
          </RequirePermission>
        }
      />

      {/* Branch Manage — Stock Management */}
      <Route
        path="/branch/stock/upload"
        element={
          <RequirePermission allow={featureAccess.stockManagement(role)}>
            <StockUploadPage />
          </RequirePermission>
        }
      />

      {/* Branch Manage — Pathway Upload */}
      <Route
        path="/branch/pathway/upload"
        element={
          <RequirePermission allow={featureAccess.pathwayUpload(role)}>
            <PathwayUploadPage />
          </RequirePermission>
        }
      />

      {/* Demand Factor — branch manager (own branch), head office and master */}
      <Route
        path={DEMAND_FACTOR_PATH}
        element={
          <RequirePermission allow={featureAccess.demandFactor(role)}>
            <DemandFactorPage />
          </RequirePermission>
        }
      />

      {/* สายส่ง — route management */}
      <Route
        path={ROUTE_MANAGEMENT_PATH}
        element={
          <RequirePermission allow={featureAccess.routeManagement(role)}>
            <RouteManagementPage />
          </RequirePermission>
        }
      />

      {/* Branch Manage — Vehicles */}
      <Route
        path={VEHICLES_PATH}
        element={
          <RequirePermission allow={featureAccess.vehicleManagement(role)}>
            <VehiclePage />
          </RequirePermission>
        }
      />

      {/* PO โมเดิร์นเทรด — head office: รายการ PO (W1) + นำเข้าไฟล์ PO (W2) */}
      <Route
        path="/purchase-order"
        element={
          <RequirePermission allow={featureAccess.poManage(role)}>
            <PurchaseOrderListPage />
          </RequirePermission>
        }
      />
      <Route
        path="/purchase-order/import"
        element={
          <RequirePermission allow={featureAccess.poManage(role)}>
            <PurchaseOrderImportPage />
          </RequirePermission>
        }
      />

      {/* PO โมเดิร์นเทรด — branch: PO ของสาขา (W7) */}
      <Route
        path="/purchase-order/branch"
        element={
          <RequirePermission allow={featureAccess.poBranch(role)}>
            <BranchPurchaseOrderPage />
          </RequirePermission>
        }
      />

      {/* BDC (7-Eleven DC) — import EDI, queue, shipment detail, settings */}
      {[BDC_PATH, BDC_QUEUE_PATH].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequirePermission allow={featureAccess.bdc(role)}>
              <BdcQueuePage />
            </RequirePermission>
          }
        />
      ))}
      <Route
        path={BDC_IMPORT_PATH}
        element={
          <RequirePermission allow={featureAccess.bdcDesk(role)}>
            <BdcImportPage />
          </RequirePermission>
        }
      />
      <Route
        path={BDC_SHIPMENT_PATH}
        element={
          <RequirePermission allow={featureAccess.bdc(role)}>
            <BdcShipmentPage />
          </RequirePermission>
        }
      />
      <Route
        path={BDC_DISPATCH_PATH}
        element={
          <RequirePermission allow={featureAccess.bdc(role)}>
            <BdcDispatchPage />
          </RequirePermission>
        }
      />
      <Route
        path={BDC_STOCK_DAY_PATH}
        element={
          <RequirePermission allow={featureAccess.bdc(role)}>
            <BdcStockDayPage />
          </RequirePermission>
        }
      />
      {[
        { path: BDC_DOCUMENTS_PATH, page: <BdcDocumentsPage /> },
        { path: BDC_INVOICES_PATH, page: <BdcInvoicesPage /> },
        { path: BDC_EDI_PATH, page: <BdcEdiPage /> },
        { path: BDC_CREDIT_NOTES_PATH, page: <BdcCreditNotesPage /> },
        { path: BDC_CREDIT_NOTE_PATH, page: <BdcCreditNotePage /> },
      ].map(({ path, page }) => (
        <Route
          key={path}
          path={path}
          element={<RequirePermission allow={featureAccess.bdc(role)}>{page}</RequirePermission>}
        />
      ))}
      <Route
        path={BDC_BILLINGS_PATH}
        element={
          <RequirePermission allow={featureAccess.bdcBilling(role)}>
            <BdcBillingsPage />
          </RequirePermission>
        }
      />
      <Route
        path={BDC_SETTINGS_PATH}
        element={
          <RequirePermission allow={featureAccess.bdc(role)}>
            <BdcSettingsPage />
          </RequirePermission>
        }
      />
      <Route
        path={BDC_DASHBOARD_PATH}
        element={
          <RequirePermission allow={featureAccess.bdc(role)}>
            <BdcDashboardPage />
          </RequirePermission>
        }
      />
      <Route
        path={BDC_DASHBOARD_STORES_PATH}
        element={
          <RequirePermission allow={featureAccess.bdc(role)}>
            <BdcDashboardStoresPage />
          </RequirePermission>
        }
      />

      {/* ตั้งค่าระบบ — MASTER only */}
      <Route
        path={SYSTEM_SETTINGS_PATH}
        element={
          <RequirePermission allow={featureAccess.systemSettings(role)}>
            <SystemSettingsPage />
          </RequirePermission>
        }
      />

      {/* Event Log */}
      <Route path="/event-logs" element={<EventLogsPage />} />

      {/* Dedicated Permission Denied route */}
      <Route path="/permission-denied" element={<PermissionDeniedPage />} />
    </Routes>
  );
}

export default Routing;
